import { useState, useMemo, useEffect } from 'react';
import ComissaoService from '../../services/comissaoService'; 
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faSearch, faInfoCircle, faThumbsUp, faThumbsDown, faSync, faClock, faTimes, faCheckCircle, faBan, faSpinner 
} from '@fortawesome/free-solid-svg-icons';

export interface ItemFinanceiroDTO {
  idEstorno?: number;
  idCalculo: number;
  idParceiro: number;
  nomeParceiro: string;
  contrato: string;
  valor: number;
  descricao: string;
  motivo: string;
  dataEvento: string;
  status: string; 
}

interface OrigemDTO {
  id: number;
  nome: string;
}

interface FiltrosDTO {
  dataInicio: string;
  dataFim: string;
  status: string;
  tipoOrigem: string;
  idOrigem: string;
  contrato?: string;
}

const CustomAlert = ({ message, type, onClose }: { message: string, type: 'success' | 'warning' | 'error', onClose: () => void }) => (
  <div className={`alert alert-${type === 'error' ? 'danger' : type} alert-dismissible fade show fixed-top m-3 shadow`} role="alert" style={{ maxWidth: '500px', zIndex: 1060 }}>
    {message}
    <button type="button" className="btn-close" onClick={onClose}></button>
  </div>
);

const Spinner = ({ loading }: { loading: boolean }) => (
  loading ? <div className="text-center p-4"><div className="spinner-border text-primary" role="status"></div></div> : null
);

function HistoricoEstornoComponent() {
  
  useEffect(() => {
    const link = document.createElement('link');
    link.href = "https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css";
    link.rel = "stylesheet";
    document.head.appendChild(link);
    return () => { if(document.head.contains(link)) document.head.removeChild(link); };
  }, []);

  const comissaoService = useMemo(() => new ComissaoService(), []);

  const [alert, setAlert] = useState<{ message: string, type: 'success' | 'warning' | 'error' } | null>(null);
  const [loading, setLoading] = useState(false);
  const [filtros, setFiltros] = useState<FiltrosDTO>({
      dataInicio: '',
      dataFim: '',
      status: 'PENDENTE', 
      tipoOrigem: '', 
      idOrigem: '',
      contrato: '' 
  });
  const [listaItens, setListaItens] = useState<ItemFinanceiroDTO[]>([]);
  const [showModalMotivo, setShowModalMotivo] = useState(false);
  const [textoMotivo, setTextoMotivo] = useState('');
  const [showModalReprovar, setShowModalReprovar] = useState(false);
  const [itemParaAcao, setItemParaAcao] = useState<ItemFinanceiroDTO | null>(null);
  const [justificativaAcao, setJustificativaAcao] = useState('');

  const buscarItensFinanceiros = async () => {
    setLoading(true);
    setListaItens([]);
    try {
      const response = await comissaoService.axios.get('/financeiro/itens', {
        params: {
            dataInicio: filtros.dataInicio || null,
            dataFim: filtros.dataFim || null,
            contrato: filtros.contrato || null,
            status: filtros.status || null, 
            idParceiro: filtros.idOrigem || null,
            tipoOrigem: filtros.tipoOrigem || null
        }
      });
      setListaItens(response.data);
    } catch (err) {
      setAlert({ message: 'Erro ao buscar dados.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleAprovar = async (item: ItemFinanceiroDTO) => {
      if(!window.confirm(`Confirmar aprovação do estorno: ${item.contrato}?`)) return;
      setLoading(true);
      try {
          await comissaoService.axios.post(`/financeiro/estornar/${item.idCalculo}/2?motivo=${encodeURIComponent('Aprovação manual via sistema')}`);
          setAlert({ message: 'Estorno concluído com sucesso!', type: 'success' });
          buscarItensFinanceiros(); 
      } catch (error) {
          setAlert({ message: 'Erro ao aprovar.', type: 'error' });
      } finally {
          setLoading(false);
      }
  };

  const confirmarReprovacao = async () => {
      if(!itemParaAcao || !justificativaAcao) {
          setAlert({ message: 'Justificativa obrigatória.', type: 'warning' });
          return;
      }
      setLoading(true);
      try {
          await comissaoService.axios.post(`/financeiro/estornar/${itemParaAcao.idCalculo}/2?motivo=${encodeURIComponent(justificativaAcao)}`);
          setAlert({ message: 'Solicitação cancelada.', type: 'success' });
          setShowModalReprovar(false);
          buscarItensFinanceiros();
      } catch (error) {
          setAlert({ message: 'Erro ao cancelar solicitação.', type: 'error' });
      } finally {
          setLoading(false);
      }
  };

  const renderStatusBadge = (status: string) => {
    switch (status) {
      case 'PENDENTE':
        return <span className="badge bg-warning text-dark border border-warning"><FontAwesomeIcon icon={faClock} className="me-1"/> Pendente</span>;
      case 'EM_PROCESSAMENTO':
        return <span className="badge bg-info text-dark"><FontAwesomeIcon icon={faSpinner} spin className="me-1"/> Processando</span>;
      case 'CONCLUIDO':
        return <span className="badge bg-success"><FontAwesomeIcon icon={faCheckCircle} className="me-1"/> Concluído</span>;
      case 'CANCELADO':
        return <span className="badge bg-danger"><FontAwesomeIcon icon={faBan} className="me-1"/> Cancelado</span>;
      default:
        return <span className="badge bg-light text-dark border">{status}</span>;
    }
  };

  const visualizarMotivo = (motivo: string) => {
      setTextoMotivo(motivo || 'Não informado.');
      setShowModalMotivo(true);
  };

  return (
    <>
      {alert && (<CustomAlert message={alert.message} type={alert.type} onClose={() => setAlert(null)} />)}
      <div className="m-3">
        <div className="mb-4 d-flex justify-content-between align-items-center">
            <div>
                <h4 className="fw-bold mb-0">Gestão de Estornos</h4>
                <small className="text-muted">Gestão de solicitações de estorno</small>
            </div>
            <button className="btn btn-outline-primary btn-sm" onClick={buscarItensFinanceiros}>
                <FontAwesomeIcon icon={faSync} className="me-2"/> Atualizar
            </button>
        </div>

        <div className="card shadow-sm border-0">
            <div className="card-body">
              <div className="p-3 bg-light rounded border mb-4">
                <div className="row g-2 align-items-end">
                    <div className="col-md-3">
                        <label className="form-label small fw-bold">Filtrar por Status</label>
                        <select className="form-select form-select-sm" value={filtros.status} onChange={e => setFiltros({...filtros, status: e.target.value})}>
                            <option value="">Todos os Status</option>
                            <option value="PENDENTE">Pendente</option>
                            <option value="EM_PROCESSAMENTO">Em Processamento</option>
                            <option value="CONCLUIDO">Concluído</option>
                            <option value="CANCELADO">Cancelado</option>
                        </select>
                    </div>
                    <div className="col-md-3">
                        <label className="form-label small fw-bold">Nº do Contrato</label>
                        <input type="text" className="form-control form-control-sm" value={filtros.contrato} onChange={e => setFiltros({...filtros, contrato: e.target.value})} placeholder="Número..."/>
                    </div>
                    <div className="col-md-3">
                        <button className="btn btn-primary btn-sm w-100" onClick={buscarItensFinanceiros}>
                            <FontAwesomeIcon icon={faSearch} className="me-2" /> Filtrar Lista
                        </button>
                    </div>
                </div>
              </div>

              {loading ? <Spinner loading={loading} /> : (
                <div className="table-responsive border rounded">
                  <table className="table table-hover mb-0 align-middle">
                    <thead className="table-secondary small text-uppercase">
                      <tr>
                        <th className="text-center">ID</th>
                        <th>Parceiro</th>
                        <th className="text-center">Contrato</th>
                        <th className="text-center">Justificativa</th>
                        <th className="text-center">Data</th>
                        <th className="text-end">Valor</th>
                        <th className="text-center">Status</th>
                        <th className="text-center">Ações de Gestão</th>
                      </tr>
                    </thead>
                    <tbody>
                      {listaItens.length > 0 ? listaItens.map((item) => (
                        <tr key={item.idCalculo}>
                          <td className="text-center text-muted small">{item.idCalculo}</td>
                          <td>{item.nomeParceiro}</td>
                          <td className="text-center fw-bold">{item.contrato}</td>
                          <td className="text-center">
                              <button className="btn btn-link btn-sm p-0" onClick={() => visualizarMotivo(item.motivo)}>
                                  <FontAwesomeIcon icon={faInfoCircle} className="text-info"/>
                              </button>
                          </td>
                          <td className="text-center">{new Date(item.dataEvento).toLocaleDateString('pt-BR')}</td>
                          <td className="text-end fw-bold">{item.valor?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</td>
                          <td className="text-center">
                              {renderStatusBadge(item.status)}
                          </td>
                          <td className="text-center">
                              {(item.status === 'PENDENTE' || item.status === 'EM_PROCESSAMENTO') ? (
                                  <div className="btn-group btn-group-sm">
                                      <button className="btn btn-success" onClick={() => handleAprovar(item)} title="Concluir Estorno">
                                          <FontAwesomeIcon icon={faThumbsUp} />
                                      </button>
                                      <button className="btn btn-danger" onClick={() => { setItemParaAcao(item); setJustificativaAcao(''); setShowModalReprovar(true); }} title="Cancelar Solicitação">
                                          <FontAwesomeIcon icon={faThumbsDown} />
                                      </button>
                                  </div>
                              ) : <span className="text-muted small">-</span>}
                          </td>
                        </tr>
                      )) : (
                        <tr><td colSpan={8} className="text-center py-4 text-muted">Nenhum registro encontrado.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        
        {/* Modal de Motivo */}
        {showModalMotivo && (
            <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}>
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content shadow border-0">
                        <div className="modal-header bg-info text-white py-2">
                            <h6 className="modal-title fw-bold">Visualizar Justificativa</h6>
                            <button type="button" className="btn-close btn-close-white" onClick={() => setShowModalMotivo(false)}></button>
                        </div>
                        <div className="modal-body p-4 bg-light">
                            <div className="p-3 bg-white rounded border">
                                <em>"{textoMotivo}"</em>
                            </div>
                        </div>
                        <div className="modal-footer bg-light border-0">
                            <button className="btn btn-sm btn-secondary px-4" onClick={() => setShowModalMotivo(false)}>Fechar</button>
                        </div>
                    </div>
                </div>
            </div>
        )}

        {/* Modal de Reprovação (Cancelamento) */}
        {showModalReprovar && (
            <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}>
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content shadow border-0">
                        <div className="modal-header bg-danger text-white py-2">
                            <h6 className="modal-title fw-bold"><FontAwesomeIcon icon={faTimes} className="me-2"/> Reprovar / Cancelar Estorno</h6>
                            <button type="button" className="btn-close btn-close-white" onClick={() => setShowModalReprovar(false)}></button>
                        </div>
                        <div className="modal-body p-4">
                            <p className="small mb-2">Contrato: <strong>{itemParaAcao?.contrato}</strong></p>
                            <textarea className="form-control form-control-sm" rows={3} value={justificativaAcao} 
                                onChange={e => setJustificativaAcao(e.target.value)} placeholder="Motivo da reprovação..."></textarea>
                        </div>
                        <div className="modal-footer bg-light border-0">
                            <button className="btn btn-sm btn-light border" onClick={() => setShowModalReprovar(false)}>Voltar</button>
                            <button className="btn btn-sm btn-danger px-4 fw-bold" onClick={confirmarReprovacao}>Confirmar Reprovação</button>
                        </div>
                    </div>
                </div>
            </div>
        )}
      </div>
    </>
  );
}

export default HistoricoEstornoComponent;